import React, { useEffect, useState } from 'react';
import Table from 'Components/Table';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Cell, Column } from 'react-table';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import clsx from 'clsx';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters';
import { QF_MODIFIED, QF_STATUS } from 'Hooks/rule-quickfilters/helper';
import { RoleService } from 'Services';
import { useQueryParam } from 'Hooks/useQueryParam';
import useGetRuleGroups from 'Hooks/query-hooks/global-rules/useGetRuleGroups';
import RouteLoading from 'Components/RouteLoading';
import { keyBy } from 'lodash';
import { GLOBAL_RULE_TABLE_VIEW, GLOBAL_EXECUTION_ORDER_VIEW } from 'Common/enums';
import { shouldShowExecutionOrder, getUser } from 'Selectors';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import { getRulePriority } from 'Hooks/query-hooks/helper';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import useSplitviewForm from '../Table/hooks/useSplitViewForm';
import SplitViewContent from './SplitViewContent';
import styles from './GlobalRulesTable.css';
import DeleteGroupModal from './EmptyRuleGroups/DeleteGroupModal';
import UpdateRuleGroups from './UpdateRuleGroups/UpdateRuleGroups';

type Mode = 'sandbox' | 'deployed' | 'revisionWindow' | 'overrides';

type GlobalRulesTableOptions = {
    isRevision: boolean;
    allowOverride: boolean;
    isLandlordDeployment: boolean;
    isRevisionHistoryVisible: boolean;
    isLoading: boolean;
};

type GlobalRulesTableProps = {
    columnNames: Column<Rule>[];
    rulesData: Rule[];
    mode: Mode;
    groupByColumns?: string[];
    tableName?: string;
    deploymentVersion?: number;
    emptyGroupsData?: EmptyRuleGroup[];
    rulesGroupData?: RuleGroup[];
    sandboxRules?: Rule[];
    selectViewOption: string;
    setSelectViewOption: Function;
    tableInnerHeader?: string;
    options?: Partial<GlobalRulesTableOptions>;
};

const TABLEID = 'global-rules-table';

const GlobalRulesTable = ({
    rulesData,
    columnNames,
    mode,
    groupByColumns = [],
    tableName,
    deploymentVersion,
    emptyGroupsData,
    rulesGroupData,
    sandboxRules,
    selectViewOption,
    setSelectViewOption,
    tableInnerHeader,
    options
}: GlobalRulesTableProps) => {
    useStickyTableHead(TABLEID);
    const { landlordId } = useSelector(getUser);
    const showExecutionOrder = shouldShowExecutionOrder();
    const isGlobalRuleSelectOption = selectViewOption === GLOBAL_RULE_TABLE_VIEW;
    const isExecutionOrderSelectOption = selectViewOption === GLOBAL_EXECUTION_ORDER_VIEW;
    const { data: landlordInfo } = useGetLandlordInfo();
    const {
        splitViewFormState,
        setIsOpen,
        setInitialFormState,
        setRowId
    } = useSplitviewForm<GlobalRuleSplitViewFormState>();
    const [isAddGroupModalOpen, setAddGroupModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [deleteGroup, setDeleteGroup] = useState<EmptyRuleGroup>();
    const [isGroupDelete, setIsGroupDelete] = useState<boolean>(false);
    const [isDeleteGroupOption, setDeleteGroupOption] = useState<boolean>(false);
    const ruleGroupsAPI = useGetRuleGroups();
    const { t } = useTranslation();
    const readOnly = mode === 'deployed' || mode === 'revisionWindow';
    const qfHook = useGetRuleQuickFilters({
        quickFilters: mode === 'deployed' ? [QF_STATUS.id] : [QF_MODIFIED.id, QF_STATUS.id],
        initialFilters: [
            {
                id: QF_STATUS.id,
                activeId: readOnly ? [QF_STATUS.options.active] : []
            }
        ]
    });

    const columns = React.useMemo<Column<Rule>[]>(() => columnNames, [columnNames]);
    const sandboxRulesKeyed = keyBy(sandboxRules, 'id');
    rulesData.forEach((rule) => {
        rule.isTenantOverride = sandboxRulesKeyed[rule.id] && sandboxRulesKeyed[rule.id].isTenantOverride;
        /** @note - Rule Type should be stored in the database. */
        if (rule.isTenantOverride) {
            rule.ruleType = t('Override');
        }
        if (showExecutionOrder && !rule?.priority) {
            rule.priority = getRulePriority(t, rule);
        } else if (rule.expression) {
            rule.ruleType = t('Advanced');
        } else {
            rule.ruleType = t('Simple');
        }
    });
    const data = React.useMemo(() => {
        return !isGlobalRuleSelectOption && landlordInfo?.landlordId
            ? rulesData.filter((rule: Rule) => rule.allowOverrides === false)
            : rulesData;
    }, [rulesData, landlordInfo, isGlobalRuleSelectOption]);
    const isTenant = landlordId === null;

    const hideFromTenantOverrideRules = React.useMemo(() => {
        return isTenant ? data.filter((rules: Rule) => !(rules.allowOverrides && rules.hiddenFromTenant)) : data;
    }, [data, isTenant]);

    const updateSplitViewId = React.useCallback(
        (val) => {
            setRowId?.(val);
            setIsOpen?.(false);
        },
        [setRowId, setIsOpen]
    );
    const {
        parsed: { selectedRowId }
    } = useQueryParam();

    const handleTrackEventClick = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Global Rules - View Rule',
                text: 'View Rule'
            }
        });
    };

    useEffect(() => {
        setRowId?.(selectedRowId);
    }, [selectedRowId, setRowId]);

    if (ruleGroupsAPI.isError) {
        return (
            <div data-testid='group-error-id'>
                <p>{t('Error Fetching', { fieldName: 'rule group types' })}</p>
            </div>
        );
    }

    if (ruleGroupsAPI.isFetching || ruleGroupsAPI.isLoading) {
        return <RouteLoading />;
    }
    const sortOptions = [
        { id: 'priority', desc: false },
        { id: 'id', desc: false }
    ];
    const globalSortOptions = [
        {
            id: 'id',
            desc: false
        }
    ];

    const revisionHeader = options?.isRevision && isExecutionOrderSelectOption ? tableInnerHeader : undefined;
    return (
        <div className={clsx(styles.tableContainer, options?.isRevision && styles.revision)}>
            {isAddGroupModalOpen ? (
                <UpdateRuleGroups
                    isEditOpen={isAddGroupModalOpen}
                    deleteGroup={deleteGroup}
                    groupData={ruleGroupsAPI.data}
                    closeEditModal={() => {
                        setAddGroupModalOpen(false);
                        setDeleteGroupOption(false);
                    }}
                    selectedGroup={selectedGroup}
                    onDeleteAction={() => setIsGroupDelete(true)}
                    isDeleteGroupOption={isDeleteGroupOption}
                    rulesGroupData={rulesGroupData}
                    emptyGroupsData={emptyGroupsData}
                />
            ) : null}

            <Table
                handleEditRow={(cell: Cell) => {
                    setAddGroupModalOpen(true);
                    setSelectedGroup(rulesGroupData?.find((group) => group.name === cell?.value)?.name || '');
                }}
                tableName={tableName}
                innerTableHeader={revisionHeader}
                isLoading={options?.isLoading}
                manualSplitView
                controlledSplitViewId={splitViewFormState.rowId}
                updateSplitViewId={updateSplitViewId}
                icon={{
                    showIcon: true
                }}
                expand={{
                    canExpand: true,
                    expandAll: true
                }}
                quickFilters={qfHook.filters}
                initialState={{
                    groupBy: groupByColumns,
                    sortBy: globalSortOptions,
                    globalFilter: {
                        quickFilters: qfHook.initialQuickFilters
                    }
                }}
                dataTestId={TABLEID}
                columns={columns}
                data={hideFromTenantOverrideRules}
                clickableRow
                noDataMessage={
                    options?.isLandlordDeployment ? t('No overridable landlord rules found') : t('No rules found.')
                }
                splitViewContent={(props) => {
                    handleTrackEventClick();
                    return (
                        <SplitViewContent
                            setRowId={setRowId}
                            setInitialFormState={setInitialFormState}
                            splitViewFormState={splitViewFormState}
                            setIsOpen={setIsOpen}
                            dataTestId='gr-split-view'
                            deploymentVersion={deploymentVersion}
                            mode={mode}
                            selectViewOption={selectViewOption}
                            options={{
                                readOnly: readOnly || !RoleService.hasEditRuleOrBulkUploadAccess,
                                allowOverride: options?.allowOverride,
                                isRevisionHistoryVisible: options?.isRevisionHistoryVisible,
                                isLandlordDeployment: options?.isLandlordDeployment
                            }}
                            {...props}
                        />
                    );
                }}
                showExpandableSearch
                showMultiSort={isGlobalRuleSelectOption}
                showRichFilter
                emptyGroupsData={emptyGroupsData}
                rulesGroupData={rulesGroupData}
                handleDeleteRow={(delGroup: EmptyRuleGroup) => {
                    setDeleteGroup(delGroup);
                    setSelectedGroup(delGroup.name);
                    setAddGroupModalOpen(true);
                    setDeleteGroupOption(true);
                }}
                showRuleDropdown={showExecutionOrder}
                selectViewOption={selectViewOption}
                setSelectViewOption={setSelectViewOption}
                dynamicGroupBy={groupByColumns}
                sortOptions={!isGlobalRuleSelectOption ? sortOptions : globalSortOptions}
            />

            {isGroupDelete && deleteGroup ? (
                <DeleteGroupModal
                    onClose={() => {
                        setAddGroupModalOpen(false);
                        setIsGroupDelete(false);
                        setSelectedGroup('');
                    }}
                    deleteGroupId={deleteGroup.id}
                    isGroupDelete={isGroupDelete}
                />
            ) : null}
        </div>
    );
};

export default GlobalRulesTable;
